import type { ReactNode } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';

/** The backend has no configuration for the requested realm (`GET /config` answered 404). */
export class RealmNotConnectedError extends Error {}

interface StartupErrorProps {
  title: string;
  children: ReactNode;
  /** Shows a "Reload Page" button, for failures a retry can fix. */
  reloadable?: boolean;
}

/** Full-page error card rendered by `main.tsx` when the app cannot boot, inside the package's MUI
 *  providers. i18n is not initialised at this point, so the copy is English. Kept identical in shell
 *  and webapp, which share no code. */
export function StartupError({ title, children, reloadable = false }: StartupErrorProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default',
        m: 0,
      }}
    >
      {/* GB30 renders every elevation as `none`, so the card carries its own border + shadow. */}
      <Paper variant="outlined" sx={{ maxWidth: 520, p: 8 }}>
        {/* GB30 disables MUI's `h6` variant, so the h2 heading borrows `h5`. */}
        <Typography variant="h5" component="h2" color="error" sx={{ mb: 4 }}>
          {title}
        </Typography>
        {children}
        {reloadable && (
          <Button variant="contained" sx={{ mt: 4 }} onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        )}
      </Paper>
    </Box>
  );
}

/** The card for a failed bootstrap: a dedicated message for an unconnected realm, else a generic one. */
export function StartupFailure({ error, realm }: { error: unknown; realm: string }) {
  return error instanceof RealmNotConnectedError ? (
    <StartupError title="Realm Not Connected">
      <Typography sx={{ lineHeight: 1.5 }}>
        The realm <code>{realm}</code> is not connected to Corporate User Access Management yet. Contact the IAM Service
        Team for onboarding.
      </Typography>
    </StartupError>
  ) : (
    <StartupError title="Startup Failed" reloadable>
      <Typography sx={{ lineHeight: 1.5 }}>
        Corporate User Access Management could not be started. Reload the page to try again. If the problem persists,
        contact your administrator.
      </Typography>
    </StartupError>
  );
}
